import { useState } from 'react';
import { bulkRemind, rolloverProject, setAnnouncement, setRecurringReminder, exportProject } from '../api';

// ─── Bulk Remind Modal ────────────────────────────────────────────────────────

export function BulkRemindModal({ project, playerCount, onClose }) {
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handle = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await bulkRemind(project.projectId, message);
      setResult(res);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h3>📧 Remind All</h3>
        {result ? (
          <div>
            <div className="alert alert-success" style={{ marginBottom: 16 }}>
              ✓ Sent {result.sent} reminder{result.sent !== 1 ? 's' : ''}
              {result.skipped > 0 && ` · ${result.skipped} skipped (paid or no email)`}
            </div>
            {result.errors?.length > 0 && (
              <div className="alert alert-error">
                {result.errors.map(e => `${e.player}: ${e.error}`).join('\n')}
              </div>
            )}
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={onClose}>Done</button>
            </div>
          </div>
        ) : (
          <form onSubmit={handle}>
            <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>
              Sends a payment reminder to all parents with an outstanding balance in <strong>{project.name}</strong>.
            </p>
            {error && <div className="alert alert-error">{error}</div>}
            <div className="form-group">
              <label>Custom Message (optional)</label>
              <textarea rows={3} value={message} onChange={e => setMessage(e.target.value)}
                placeholder="e.g. Payment is due by the end of the month." />
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Sending…' : 'Send Reminders'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ─── Season Rollover Modal ────────────────────────────────────────────────────

export function RolloverModal({ project, onClose, onSave }) {
  const [form, setForm] = useState({
    newName: `${project.name} (New Season)`,
    newSeason: '',
    startingBalance: '',
    copyEmails: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handle = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await rolloverProject(project.projectId, {
        ...form,
        startingBalance: form.startingBalance === '' ? -1 : parseFloat(form.startingBalance),
      });
      onSave(res.project);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h3>🔄 New Season Rollover</h3>
        <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>
          Creates a new project with the same players and zeroed balances. The original project is kept as a historical record.
        </p>
        {error && <div className="alert alert-error">{error}</div>}
        <form onSubmit={handle}>
          <div className="form-group">
            <label>New Project Name</label>
            <input value={form.newName} onChange={set('newName')} required autoFocus />
          </div>
          <div className="form-group">
            <label>New Season</label>
            <input value={form.newSeason} onChange={set('newSeason')} placeholder="e.g. 2026-27" />
          </div>
          <div className="form-group">
            <label>Starting Balance for Each Player ($)</label>
            <input type="number" step="0.01" value={form.startingBalance} onChange={set('startingBalance')}
              placeholder="Leave blank to keep each player's current balance" />
            <div className="helper-text">Leave blank to carry forward each player's existing starting balance. Enter 0 to start fresh.</div>
          </div>
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, textTransform: 'none', fontSize: 14, fontWeight: 500 }}>
              <input type="checkbox" checked={form.copyEmails} onChange={e => setForm(f => ({ ...f, copyEmails: e.target.checked }))}
                style={{ width: 16, height: 16, flexShrink: 0 }} />
              Copy parent email addresses to new season
            </label>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Creating…' : 'Create New Season'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Announcement Modal ───────────────────────────────────────────────────────

export function AnnouncementModal({ project, onClose, onSave }) {
  const [text, setText] = useState(project.announcement?.text || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handle = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await setAnnouncement(project.projectId, text);
      onSave();
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleClear = async () => {
    setLoading(true);
    try { await setAnnouncement(project.projectId, ''); onSave(); }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h3>📢 Project Announcement</h3>
        <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>
          This message appears as a banner on every parent's balance page for this project.
        </p>
        {error && <div className="alert alert-error">{error}</div>}
        <form onSubmit={handle}>
          <div className="form-group">
            <label>Announcement</label>
            <textarea rows={3} value={text} onChange={e => setText(e.target.value)} autoFocus
              placeholder="e.g. Tournament fees are due by Friday. Contact Jeff with questions." />
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            {project.announcement?.text && (
              <button type="button" className="btn btn-ghost" style={{ color: 'var(--red)' }} onClick={handleClear} disabled={loading}>
                Clear
              </button>
            )}
            <button type="submit" className="btn btn-primary" disabled={loading || !text.trim()}>
              {loading ? 'Saving…' : 'Post Announcement'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Recurring Reminder Modal ─────────────────────────────────────────────────

export function RecurringReminderModal({ project, onClose, onSave }) {
  const existing = project.recurringReminder;
  const [form, setForm] = useState({
    enabled: existing?.enabled || false,
    dayOfMonth: existing?.dayOfMonth || 1,
    message: existing?.message || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handle = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await setRecurringReminder(project.projectId, form);
      onSave();
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h3>⏰ Recurring Reminders</h3>
        <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>
          Automatically send payment reminders to all parents with outstanding balances on a set day each month.
        </p>
        {error && <div className="alert alert-error">{error}</div>}
        <form onSubmit={handle}>
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, textTransform: 'none', fontSize: 14, fontWeight: 600 }}>
              <input type="checkbox" checked={form.enabled} onChange={e => setForm(f => ({ ...f, enabled: e.target.checked }))}
                style={{ width: 16, height: 16, flexShrink: 0 }} />
              Enable monthly automatic reminders
            </label>
          </div>
          {form.enabled && (
            <>
              <div className="form-group">
                <label>Day of Month</label>
                <input type="number" min="1" max="28" value={form.dayOfMonth} onChange={set('dayOfMonth')} />
                <div className="helper-text">Reminders will send on this day each month at 9am UTC. Use 28 or less to avoid month-end issues.</div>
              </div>
              <div className="form-group">
                <label>Custom Message (optional)</label>
                <textarea rows={2} value={form.message} onChange={set('message')}
                  placeholder="e.g. Monthly payment reminder — please pay by the 5th." />
              </div>
            </>
          )}
          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Export utility ───────────────────────────────────────────────────────────

export async function downloadExport(project) {
  const data = await exportProject(project.projectId);
  const headers = ['Player', 'Date', 'Type', 'Description', 'Amount', 'Balance'];
  const csvRows = [
    [`${data.projectName}${data.season ? ` - ${data.season}` : ''}`],
    [`Exported: ${new Date().toLocaleDateString()}`],
    [],
    headers,
    ...data.rows.map(r => [r.player, r.date, r.type, r.description, r.amount, r.balance]),
  ];
  const csv = csvRows.map(row => row.map(v => `"${(v || '').toString().replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${data.projectName.replace(/[^a-z0-9]/gi, '_')}_export.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
